import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'ru' | 'kz';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navbar
    'nav.technology': 'Technology',
    'nav.marketplace': 'Discover',
    'nav.founders': 'Founders',
    'nav.business': 'For Business',
    'nav.contact': 'Contact',
    'nav.joinWaitlist': 'Join Waitlist',
    
    // Hero
    'hero.badge': 'Beta Access Open',
    'hero.title': 'Try it before you wear it.',
    'hero.subtitle': 'Your digital twin, powered by AI. Join the exclusive beta to experience the future of fashion.',
    'hero.cta.primary': 'Get Early Access',
    'hero.cta.secondary': 'See How It Works',
    'hero.original': 'Original',
    'hero.avatar': 'IDK Avatar',
    'hero.wearingNow': 'Wearing Now',
    'hero.blackBlazer': 'Black Blazer',
    'hero.trust.location': '🇰🇿 Based in Almaty',
    'hero.trust.gemini': '⚡ Powered by frontier AI',
    'hero.trust.launch': '🚀 Launching 2026',
    'hero.store.appStore': '📱 Coming soon to App Store',
    'hero.store.googlePlay': '📱 Coming soon to Google Play',
    
    // Validation Bar
    'validation.onWaitlist': 'on Waitlist',
    
    // Technology
    'tech.title.part1': 'The technology behind',
    'tech.title.part2': 'the magic.',
    'tech.subtitle': 'Cutting-edge AI meets high fashion. Experience try-on technology that feels like magic.',
    'tech.feature1.title': 'AI Virtual Try-On',
    'tech.feature1.desc': 'Our diffusion models generate photorealistic try-ons in seconds. See exactly how any garment looks on your digital twin.',
    'tech.feature2.title': 'Digitize Your Wardrobe',
    'tech.feature2.desc': 'Snap a photo of any clothing item. Our AI extracts it perfectly for mixing and matching.',
    'tech.feature3.title': 'Get Feedback',
    'tech.feature3.desc': 'Share looks with friends and get instant reactions. Cop or Drop?',
    'tech.livePoll': 'Live Poll',
    'tech.votes': 'votes',
    
    // Marketplace
    'marketplace.title.part1': 'Shop with',
    'marketplace.title.part2': 'confidence.',
    'marketplace.subtitle': 'Try before you buy. Our collection lets you see exactly how luxury items look on your avatar.',
    'marketplace.comingSoon': 'Brand collaborations coming soon',
    'marketplace.comingSoonBadge': 'Coming Soon',
    'marketplace.previewBanner': 'Discover preview · Shopping launches Q4 2026',
    'marketplace.previewBadge': 'Preview',

    // How It Works
    'how.title.part1': 'Three steps to your',
    'how.title.part2': 'digital twin.',
    'how.step1.title': 'Upload two photos',
    'how.step1.desc': 'One full-body shot and one close-up of your face. That\'s all we need.',
    'how.step2.title': 'AI builds your avatar',
    'how.step2.desc': 'Our diffusion models create a photorealistic digital twin in under 20 seconds.',
    'how.step3.title': 'Try anything on',
    'how.step3.desc': 'Pick any item from your wardrobe or any store — and see it on you instantly.',

    // Privacy
    'privacy.title.part1': 'Your photos stay',
    'privacy.title.part2': 'yours.',
    'privacy.subtitle': 'We built IDK with privacy as the foundation, not an afterthought.',
    'privacy.card1.title': 'Your wardrobe stays yours',
    'privacy.card1.desc': 'Wardrobe photos are private to your account. Generated avatars only appear where you choose to share them — same as a profile photo.',
    'privacy.card2.title': 'Never used to train AI',
    'privacy.card2.desc': 'Photos are sent over HTTPS to our Firebase backend and to Google Cloud AI (Gemini) for AI features. Google does not retain or train on them under our enterprise terms.',
    'privacy.card3.title': 'Delete anytime',
    'privacy.card3.desc': 'One tap to delete your avatar and all associated data. No questions asked.',

    // FAQ (consumer)
    'faq.title': 'Frequently asked.',
    'faq.q1': 'When does IDK launch?',
    'faq.a1': "We're opening early access in Q3 2026. Join the waitlist to get your invite first.",
    'faq.q2': 'What devices does it work on?',
    'faq.a2': 'iOS 16+ and Android 12+. iPhone/iPad app launching first, Android follows in Q4 2026.',
    'faq.q3': 'How accurate is the try-on?',
    'faq.a3': "We use frontier diffusion models with fashion-specific fine-tuning. Try-ons reflect real fabric drape, body proportions, and lighting. It's the closest thing to a real fitting room.",
    'faq.q4': 'Is my data safe?',
    'faq.a4': 'Yes. Photos travel over a secure HTTPS connection to our Firebase backend and to Google Gemini for AI processing — Google does not retain or train on them. Wardrobe photos stay private to your account; you can delete everything with one tap.',
    'faq.q5': 'Will IDK be free?',
    'faq.a5': "Yes — there's a free tier (3 single try-ons, 2 outfit try-ons, 1 avatar lifetime). Pro ($19.99/mo) gives 50 single + 25 outfit try-ons per month plus the AI Stylist. Ultra ($39.99/mo) doubles those limits, lifts travel-packing to unlimited, and runs on priority infrastructure.",
    
    // Social
    'social.title.part1': 'Fashion is',
    'social.title.part2': 'social.',
    'social.subtitle': 'Share your style, discover new looks, and try on outfits from friends — all in one place.',
    'social.stories': 'Stories',
    'social.you': 'You',
    'social.feature1.title': 'Add Friends',
    'social.feature1.desc': 'Connect with friends and build your fashion circle. See their style and get inspired.',
    'social.feature2.title': 'Share Looks',
    'social.feature2.desc': 'Post your outfits as stories like Instagram. Get reactions and comments from your crew.',
    'social.feature3.title': "Try On Friends' Looks",
    'social.feature3.desc': 'See any outfit from your feed on your digital twin. Try before you buy or borrow.',
    'social.tryOn.title': 'See their outfit on',
    'social.tryOn.you': 'you',
    'social.tryOn.desc': "Spotted a fire fit in your feed? One tap to try it on your avatar. No more guessing if it'll work for you.",
    'social.tryOn.comingSoon': 'Coming soon',
    'social.tryOn.friendLook': "Friend's look",
    'social.tryOn.onAvatar': 'On your avatar',
    
    // Founders
    'founders.title': 'Meet the Founders',
    'founders.subtitle': 'Two founders building the future of how you shop for fashion',
    'founders.bio.dmitriy': 'Co-founder & CEO. Building AI try-on infrastructure that turns one photo into your digital twin.',
    'founders.bio.arina': 'Product & design lead. Shaping how IDK feels — from first tap to every try-on.',
    'founders.social.comingSoon': 'Coming soon',
    
    // Footer
    'footer.description': 'The future of fashion try-on, powered by AI.',
    'footer.platform': 'Platform',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.howItWorks': 'Virtual Try-On',
    'footer.forBrands': 'Digital Wardrobe',
    'footer.pricing': 'Discover',
    'footer.aboutUs': 'About',
    'footer.careers': 'Careers',
    'footer.press': 'Press',
    'footer.privacyPolicy': 'Privacy',
    'footer.termsOfService': 'Terms',
    'footer.cookiePolicy': 'Cookies',
    'footer.madeWith': 'Made with',
    'footer.allRights': 'All rights reserved.',
    
    // Cookie Banner
    'cookie.message': 'We use cookies to improve your experience. By continuing, you agree to our',
    'cookie.policy': 'Cookie Policy',
    'cookie.decline': 'Decline',
    'cookie.accept': 'Accept all',
    
    // Waitlist Dialog
    'waitlist.title': 'Join the Waitlist',
    'waitlist.subtitle': 'Be among the first to experience the future of fashion try-on.',
    'waitlist.email': 'Enter your email',
    'waitlist.submit': 'Request Early Access',
    'waitlist.submitting': 'Joining...',
    'waitlist.success.title': "You're on the list!",
    'waitlist.success.message': "We'll notify you when beta access is available.",
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': "Have questions about IDK Wardrobe? We'd love to hear from you.",
    'contact.getInTouch': 'Get in Touch',
    'contact.email': 'Email',
    'contact.phoneCeo': 'Phone (CEO)',
    'contact.instagram': 'Instagram',
    'contact.linkedin': 'LinkedIn',
    'contact.inquiries': 'Inquiries',
    'contact.ceo': 'CEO — Dmitriy Bekker',
    'contact.partnerships': 'Brand Partnerships',
    'contact.press': 'Press & Media',
    'contact.faq.title': 'Frequently Asked Questions',
    'contact.faq.q1': 'When will IDK Wardrobe launch?',
    'contact.faq.a1': "We're currently in development and accepting waitlist signups. Join our waitlist to be notified when we launch and get early access.",
    'contact.faq.q2': 'How does the AI virtual try-on work?',
    'contact.faq.a2': 'Our proprietary AI technology analyzes your photo and realistically renders clothing items on your image, showing you exactly how they would look on you.',
    'contact.faq.q3': 'Is my data safe?',
    'contact.faq.a3': 'Yes, we take privacy seriously. Photos are sent over HTTPS to our Firebase backend and to Google Gemini for AI features — Google does not retain or train on them under our enterprise terms. Your wardrobe photos stay private to your account. See our Privacy Policy for the full list.',
    'contact.faq.q4': 'How can brands partner with IDK Wardrobe?',
    'contact.faq.a4': "We're always looking to partner with innovative fashion brands. Contact us at dmitriybekker@idks.app to discuss collaboration opportunities.",
    
    // 404
    '404.title': '404',
    '404.message': 'Oops! Page not found',
    '404.back': 'Return to Home',
    
    // Support
    'support.title': 'Support',
    'support.subtitle': 'Need help? We\'re here for you.',
    'support.email.title': 'Email Support',
    'support.email.desc': 'Send us an email and we\'ll get back to you as soon as possible.',
    'support.instagram.title': 'Instagram DM',
    'support.instagram.desc': 'Send us a direct message on Instagram for quick support.',
    'support.response.title': 'Response Time',
    'support.response.desc': 'We typically respond within 24-48 hours.',
    'support.faq.title': 'Frequently Asked Questions',
    'support.faq.q1': 'How does IDK Wardrobe work?',
    'support.faq.a1': 'IDK Wardrobe uses advanced AI technology to create a digital twin of yourself. You can then virtually try on clothes before purchasing them.',
    'support.faq.q2': 'Is my data secure?',
    'support.faq.a2': 'Yes. Data travels over HTTPS to our Firebase backend, and photos used for AI features are sent to Google Gemini — Google does not retain or train on them. We list every processor in our Privacy Policy.',
    'support.faq.q3': 'When will the app be available?',
    'support.faq.a3': 'We are currently in beta. Join our waitlist to be among the first to try IDK Wardrobe.',
    'support.faq.q4': 'How can I delete my account?',
    'support.faq.a4': 'You can delete your account and all associated data by contacting us at dmitriybekker@idks.app.',
    'support.faq.q5': 'Which devices are supported?',
    'support.faq.a5': 'IDK Wardrobe will be available on iOS and Android devices.',
    'support.app.title': 'App Information',
    'support.app.desc': 'IDK Wardrobe is an AI-powered virtual try-on platform that lets you see how clothes look on you before buying.',
    'footer.support': 'Support',

    // Business / B2B page
    'business.hero.badge': 'Early Access · Invite-only',
    'business.hero.title.line1': 'Try-On Infrastructure',
    'business.hero.title.line2': 'for Fashion.',
    'business.hero.subtitle': 'Drop-in API that turns any fashion catalog into photorealistic virtual try-on. Powered by frontier AI. Available for design partners in Q3 2026.',
    'business.hero.cta.primary': 'Request API Access',
    'business.hero.cta.secondary': 'View Sample Code',
    'business.hero.credibility': 'Powered by frontier AI  ·  Built on enterprise cloud  ·  99.9% uptime SLA',
    'business.pricing.launchBadge': 'Launch pricing · from Q3 2026',
    'business.nav.requestAccess': 'Request API Access',

    'business.why.title.part1': 'Built different.',
    'business.why.title.part2': 'For fashion specifically.',
    'business.why.card1.title': 'Photorealistic quality',
    'business.why.card1.text': 'Silk, lace, transparent fabrics, complex patterns — where generic try-on fails. Powered by frontier AI models.',
    'business.why.card2.title': 'Built for scale',
    'business.why.card2.text': 'Sub-3-second latency. Auto-scaling from 100 to 10M+ try-ons/month. 99.9% uptime SLA.',
    'business.why.card3.title': 'Developer-first',
    'business.why.card3.text': 'Drop-in REST API. SDKs for JavaScript, Python, Swift. 5-minute integration. Honest docs.',

    'business.how.title.part1': 'From idea to production in',
    'business.how.title.part2': '5 minutes.',
    'business.how.step1.title': 'Get your API key',
    'business.how.step1.text': 'Sign up. Receive your API key instantly. Free tier included.',
    'business.how.step2.title': 'Send two images',
    'business.how.step2.text': 'POST a garment image and a model image to our endpoint.',
    'business.how.step3.title': 'Get back photorealistic try-on',
    'business.how.step3.text': 'Display the result in your app, website, or mobile shop.',
    'business.how.codeNote': 'Same infrastructure powering our consumer app IDK:W2W — currently in private beta with fashion creators.',

    'business.useCases.title.part1': 'What you can',
    'business.useCases.title.part2': 'build.',
    'business.useCases.card1.title': 'E-commerce product pages',
    'business.useCases.card1.text': 'Add try-on to every product. Reduce returns. Increase conversion.',
    'business.useCases.card1.audience': 'DTC brands · Shopify · Mid-market retailers',
    'business.useCases.card2.title': 'Mobile shopping apps',
    'business.useCases.card2.text': 'Native iOS and Android SDKs. Real-time try-on, no webview.',
    'business.useCases.card2.audience': 'Native apps · Marketplaces',
    'business.useCases.card3.title': 'Physical retail kiosks',
    'business.useCases.card3.text': 'Interactive fitting rooms. Zero-waste catalog browsing.',
    'business.useCases.card3.audience': 'Omnichannel retail · Flagship stores',
    'business.useCases.card4.title': 'White-label platforms',
    'business.useCases.card4.text': 'Custom models fine-tuned on your catalog. Dedicated infrastructure.',
    'business.useCases.card4.audience': 'Luxury maisons · Enterprise',

    'business.pricing.title.part1': 'Simple,',
    'business.pricing.title.part2': 'transparent pricing.',
    'business.pricing.subtitle': 'Full pricing opens with general availability. Design partners get first 3 months free.',
    'business.pricing.popular': 'Most popular',
    'business.pricing.tier1.name': 'Developer',
    'business.pricing.tier1.price': 'From $99',
    'business.pricing.tier1.period': '/mo',
    'business.pricing.tier1.descriptor': 'For indie developers and DTC brands',
    'business.pricing.tier1.f1': '1,000 try-ons/month included',
    'business.pricing.tier1.f2': 'Full REST API access',
    'business.pricing.tier1.f3': 'SDKs for JS, Python, Swift',
    'business.pricing.tier1.f4': 'Community support',
    'business.pricing.tier1.cta': 'Join waitlist',
    'business.pricing.tier2.name': 'Growth',
    'business.pricing.tier2.price': 'From $1,499',
    'business.pricing.tier2.period': '/mo',
    'business.pricing.tier2.descriptor': 'For scaling retailers',
    'business.pricing.tier2.f1': '10,000+ try-ons/month',
    'business.pricing.tier2.f2': 'Shopify app + admin dashboard',
    'business.pricing.tier2.f3': 'A/B testing tools',
    'business.pricing.tier2.f4': 'Priority support + SLA',
    'business.pricing.tier2.cta': 'Join waitlist',
    'business.pricing.tier3.name': 'Enterprise',
    'business.pricing.tier3.price': 'Custom',
    'business.pricing.tier3.period': '',
    'business.pricing.tier3.descriptor': 'For luxury brands and marketplaces',
    'business.pricing.tier3.f1': 'Unlimited try-ons',
    'business.pricing.tier3.f2': 'Dedicated infrastructure',
    'business.pricing.tier3.f3': 'Custom fine-tuning on your catalog',
    'business.pricing.tier3.f4': 'SOC 2 · DPA · On-premise option',
    'business.pricing.tier3.cta': 'Talk to founders',

    'business.waitlist.title.part1': 'Join the first',
    'business.waitlist.title.part2': '20 design partners.',
    'business.waitlist.subtitle': 'Early access includes 3 months free, direct access to the founding team, and priority on roadmap decisions.',
    'business.form.email': 'Work email',
    'business.form.email.placeholder': 'you@company.com',
    'business.form.company': 'Company',
    'business.form.company.placeholder': 'Acme Apparel',
    'business.form.useCase': 'What are you building?',
    'business.form.optional': '(optional)',
    'business.form.useCase.placeholder': 'Briefly describe your use case…',
    'business.form.submit': 'Reserve your spot',
    'business.form.submitting': 'Reserving…',
    'business.form.disclaimer': "We'll email you when API access opens. No spam.",
    'business.form.success.title': "You're on the list.",
    'business.form.success.message': "We'll email you when API access opens.",
    'business.form.error.duplicate': 'This email is already on the business waitlist.',
    'business.form.error.generic': 'Something went wrong. Please try again.',

    'business.faq.title': 'Questions.',
    'business.faq.q1': 'How is this different from generic virtual try-on widgets?',
    'business.faq.a1': "We're infrastructure, not a widget. A REST API + SDKs you embed wherever you need — web, mobile app, in-store kiosk. Same core technology as consumer app IDK:W2W, available as a platform.",
    'business.faq.q2': 'What AI models do you use?',
    'business.faq.a2': 'Our premium tier runs on frontier multimodal AI. For cost-sensitive deployments, we route to a self-hosted diffusion pipeline. Enterprise customers can request fine-tuned models for their catalog.',
    'business.faq.q3': 'When does API access open?',
    'business.faq.a3': 'Design partner program opens Q3 2026. General availability in Q4 2026. Join the waitlist to secure early access.',
    'business.faq.q4': 'Are you GDPR compliant?',
    'business.faq.a4': 'Yes. We process images in-memory only, never store them beyond the response window, and provide a DPA for EU-based customers.',
    'business.faq.q5': 'Can we self-host?',
    'business.faq.a5': 'On-premise deployment is available for Enterprise customers. Contact us.',
    'business.faq.q6': "Who's behind IDK?",
    'business.faq.a6': "IDK is an independent fashion-tech team building AI try-on infrastructure. Our consumer app IDK:W2W is currently in private beta. We're incorporating a US entity in Q2 2026 — until then all contracts run through our operating entity. Design partners get direct access to the founders.",

    'business.final.title.part1': 'Ready to',
    'business.final.title.part2': 'build?',
    'business.final.docs': 'Or read our docs',

    'footer.b2bCrosslink': 'Building infrastructure for retailers?',
    'footer.b2bCrosslink.cta': 'Visit our B2B platform →',
  },
  ru: {
    // Navbar
    'nav.technology': 'Технология',
    'nav.marketplace': 'Discover',
    'nav.founders': 'Основатели',
    'nav.business': 'Для бизнеса',
    'nav.contact': 'Контакты',
    'nav.joinWaitlist': 'Записаться',
    
    // Hero
    'hero.badge': 'Бета-доступ открыт',
    'hero.title': 'Примерь, прежде чем надеть.',
    'hero.subtitle': 'Ваш цифровой двойник на базе ИИ. Присоединяйтесь к эксклюзивной бете и испытайте будущее моды.',
    'hero.cta.primary': 'Получить ранний доступ',
    'hero.cta.secondary': 'Как это работает',
    'hero.original': 'Оригинал',
    'hero.avatar': 'IDK Аватар',
    'hero.wearingNow': 'Сейчас на вас',
    'hero.blackBlazer': 'Чёрный блейзер',
    'hero.trust.location': '🇰🇿 Базируемся в Алматы',
    'hero.trust.gemini': '⚡ На передовых AI-моделях',
    'hero.trust.launch': '🚀 Запуск в 2026',
    'hero.store.appStore': '📱 Скоро в App Store',
    'hero.store.googlePlay': '📱 Скоро в Google Play',
    
    // Validation Bar
    'validation.onWaitlist': 'в списке ожидания',
    
    // Technology
    'tech.title.part1': 'Технология, стоящая за',
    'tech.title.part2': 'магией.',
    'tech.subtitle': 'Передовой ИИ встречает высокую моду. Технология примерки, которая ощущается как магия.',
    'tech.feature1.title': 'ИИ Виртуальная примерка',
    'tech.feature1.desc': 'Наши диффузионные модели генерируют фотореалистичные примерки за секунды. Смотрите, как любая вещь выглядит на вашем цифровом двойнике.',
    'tech.feature2.title': 'Оцифруйте гардероб',
    'tech.feature2.desc': 'Сфотографируйте любую вещь. Наш ИИ идеально извлечёт её для комбинирования.',
    'tech.feature3.title': 'Получайте отзывы',
    'tech.feature3.desc': 'Делитесь образами с друзьями и получайте мгновенные реакции. Круто или нет?',
    'tech.livePoll': 'Опрос',
    'tech.votes': 'голосов',
    
    // Marketplace
    'marketplace.title.part1': 'Покупайте',
    'marketplace.title.part2': 'уверенно.',
    'marketplace.subtitle': 'Примерьте перед покупкой. Наш каталог позволяет увидеть, как люксовые вещи смотрятся на вашем аватаре.',
    'marketplace.comingSoon': 'Коллаборации с брендами скоро',
    'marketplace.comingSoonBadge': 'Скоро',
    'marketplace.previewBanner': 'Превью Discover · Покупки откроются в Q4 2026',
    'marketplace.previewBadge': 'Превью',

    // How It Works
    'how.title.part1': 'Три шага до твоего',
    'how.title.part2': 'цифрового двойника.',
    'how.step1.title': 'Загрузи два фото',
    'how.step1.desc': 'Одно в полный рост и одно крупно лицо. Это всё, что нужно.',
    'how.step2.title': 'AI создаёт твой аватар',
    'how.step2.desc': 'Наши diffusion-модели создают фотореалистичного цифрового двойника за 20 секунд.',
    'how.step3.title': 'Примеряй что угодно',
    'how.step3.desc': 'Выбери любую вещь из своего гардероба или магазина — и увидь её на себе мгновенно.',

    // Privacy
    'privacy.title.part1': 'Твои фото остаются',
    'privacy.title.part2': 'твоими.',
    'privacy.subtitle': 'Мы строили IDK так, что privacy — это основа, а не что-то на сдачу.',
    'privacy.card1.title': 'Твой гардероб — только твой',
    'privacy.card1.desc': 'Фото вещей в гардеробе видны только тебе. Аватар появляется лишь там, где ты сам его покажешь — как обычная фотография профиля.',
    'privacy.card2.title': 'Не используем для обучения AI',
    'privacy.card2.desc': 'Фото передаются по HTTPS на наш Firebase-бэкенд и в Google Cloud AI (Gemini) — Google не сохраняет их и не обучает на них модели по нашему enterprise-договору.',
    'privacy.card3.title': 'Удаление в любой момент',
    'privacy.card3.desc': 'Один тап — и аватар плюс все данные удалены. Без вопросов.',

    // FAQ (consumer)
    'faq.title': 'Частые вопросы.',
    'faq.q1': 'Когда запустится IDK?',
    'faq.a1': 'Ранний доступ откроется в Q3 2026. Запишись в waitlist, чтобы получить инвайт первым.',
    'faq.q2': 'На каких устройствах работает?',
    'faq.a2': 'iOS 16+ и Android 12+. Приложение для iPhone/iPad выйдет первым, Android — в Q4 2026.',
    'faq.q3': 'Насколько точная примерка?',
    'faq.a3': 'Используем передовые diffusion-модели с fine-tuning под fashion. Примерки отражают реальную посадку ткани, пропорции тела и освещение. Ближе к реальной примерочной уже не бывает.',
    'faq.q4': 'Мои данные в безопасности?',
    'faq.a4': 'Да. Фото идут по защищённому HTTPS-каналу на наш Firebase-бэкенд и в Google Gemini для AI-обработки — Google не сохраняет их и не обучает на них модели. Фото вещей видны только тебе; всё можно удалить одним тапом.',
    'faq.q5': 'IDK будет бесплатным?',
    'faq.a5': 'Да — есть бесплатный тариф (3 примерки одной вещи, 2 примерки лука, 1 аватар — всего, не ежемесячно). Pro ($19.99/мес) даёт 50 примерок одной вещи и 25 луков в месяц + AI-стилист. Ultra ($39.99/мес) удваивает лимиты, делает безлимитной AI-сборку чемодана и работает на приоритетной инфраструктуре.',
    
    // Social
    'social.title.part1': 'Мода — это',
    'social.title.part2': 'общение.',
    'social.subtitle': 'Делитесь стилем, открывайте новые образы и примеряйте луки друзей — всё в одном месте.',
    'social.stories': 'Истории',
    'social.you': 'Вы',
    'social.feature1.title': 'Добавляйте друзей',
    'social.feature1.desc': 'Общайтесь с друзьями и создавайте модный круг. Смотрите их стиль и вдохновляйтесь.',
    'social.feature2.title': 'Делитесь образами',
    'social.feature2.desc': 'Публикуйте свои наряды как истории. Получайте реакции и комментарии от друзей.',
    'social.feature3.title': 'Примеряйте луки друзей',
    'social.feature3.desc': 'Смотрите любой наряд из ленты на своём цифровом двойнике. Примерьте до покупки или обмена.',
    'social.tryOn.title': 'Увидьте их наряд на',
    'social.tryOn.you': 'себе',
    'social.tryOn.desc': 'Заметили крутой лук в ленте? Один тап — и он на вашем аватаре. Больше не нужно гадать, подойдёт ли вам.',
    'social.tryOn.comingSoon': 'Скоро',
    'social.tryOn.friendLook': 'Лук друга',
    'social.tryOn.onAvatar': 'На вашем аватаре',
    
    // Founders
    'founders.title': 'Основатели',
    'founders.subtitle': 'Два фаундера строят будущее того, как ты покупаешь одежду',
    'founders.bio.dmitriy': 'Co-founder и CEO. Строит AI-инфраструктуру для виртуальной примерки, которая превращает одно фото в твоего цифрового двойника.',
    'founders.bio.arina': 'Продукт и дизайн. Формирует как IDK ощущается — от первого тапа до каждой примерки.',
    'founders.social.comingSoon': 'Скоро',
    
    // Footer
    'footer.description': 'Будущее модных примерок на базе ИИ.',
    'footer.platform': 'Платформа',
    'footer.company': 'Компания',
    'footer.legal': 'Юридическое',
    'footer.howItWorks': 'Виртуальная примерка',
    'footer.forBrands': 'Цифровой гардероб',
    'footer.pricing': 'Маркетплейс',
    'footer.aboutUs': 'О нас',
    'footer.careers': 'Карьера',
    'footer.press': 'Пресса',
    'footer.privacyPolicy': 'Конфиденциальность',
    'footer.termsOfService': 'Условия',
    'footer.cookiePolicy': 'Cookies',
    'footer.madeWith': 'Сделано с',
    'footer.allRights': 'Все права защищены.',
    
    // Cookie Banner
    'cookie.message': 'Мы используем cookies для улучшения вашего опыта. Продолжая, вы соглашаетесь с нашей',
    'cookie.policy': 'Политикой cookies',
    'cookie.decline': 'Отклонить',
    'cookie.accept': 'Принять все',
    
    // Waitlist Dialog
    'waitlist.title': 'Список ожидания',
    'waitlist.subtitle': 'Будьте среди первых, кто испытает будущее модных примерок.',
    'waitlist.email': 'Введите email',
    'waitlist.submit': 'Получить ранний доступ',
    'waitlist.submitting': 'Записываем...',
    'waitlist.success.title': 'Вы в списке!',
    'waitlist.success.message': 'Мы уведомим вас, когда откроется бета-доступ.',
    
    // Contact
    'contact.title': 'Свяжитесь с нами',
    'contact.subtitle': 'Есть вопросы об IDK Wardrobe? Мы будем рады услышать вас.',
    'contact.getInTouch': 'Связаться',
    'contact.email': 'Email',
    'contact.phoneCeo': 'Телефон (CEO)',
    'contact.instagram': 'Instagram',
    'contact.linkedin': 'LinkedIn',
    'contact.inquiries': 'Запросы',
    'contact.ceo': 'CEO — Дмитрий Беккер',
    'contact.partnerships': 'Партнёрство с брендами',
    'contact.press': 'Пресса и медиа',
    'contact.faq.title': 'Часто задаваемые вопросы',
    'contact.faq.q1': 'Когда запустится IDK Wardrobe?',
    'contact.faq.a1': 'Сейчас мы в разработке и принимаем заявки в лист ожидания. Запишитесь, чтобы первыми узнать о запуске и получить ранний доступ.',
    'contact.faq.q2': 'Как работает виртуальная AI-примерка?',
    'contact.faq.a2': 'Наша технология AI анализирует ваше фото и реалистично отображает одежду на нём — вы видите, как именно вещь будет смотреться на вас.',
    'contact.faq.q3': 'В безопасности ли мои данные?',
    'contact.faq.a3': 'Да, мы серьёзно относимся к приватности. Фото идут по HTTPS на наш Firebase-бэкенд и в Google Gemini для AI-функций — Google не сохраняет их и не обучает на них модели по enterprise-договору. Полный список обработчиков — в Политике конфиденциальности.',
    'contact.faq.q4': 'Как бренды могут сотрудничать с IDK Wardrobe?',
    'contact.faq.a4': 'Мы открыты к партнёрству с инновационными fashion-брендами. Напишите нам на dmitriybekker@idks.app, чтобы обсудить сотрудничество.',
    
    // 404
    '404.title': '404',
    '404.message': 'Упс! Страница не найдена',
    '404.back': 'Вернуться на главную',
    
    // Support
    'support.title': 'Поддержка',
    'support.subtitle': 'Нужна помощь? Мы здесь для вас.',
    'support.email.title': 'Поддержка по email',
    'support.email.desc': 'Напишите нам, и мы ответим как можно скорее.',
    'support.instagram.title': 'Instagram DM',
    'support.instagram.desc': 'Напишите нам в директ Instagram для быстрой поддержки.',
    'support.response.title': 'Время ответа',
    'support.response.desc': 'Обычно мы отвечаем в течение 24-48 часов.',
    'support.faq.title': 'Часто задаваемые вопросы',
    'support.faq.q1': 'Как работает IDK Wardrobe?',
    'support.faq.a1': 'IDK Wardrobe использует продвинутые ИИ-технологии для создания вашего цифрового двойника. Затем вы можете виртуально примерять одежду перед покупкой.',
    'support.faq.q2': 'Мои данные в безопасности?',
    'support.faq.a2': 'Да. Данные идут по HTTPS на наш Firebase-бэкенд, а фото для AI-функций — в Google Gemini, который не сохраняет их и не обучает на них модели. Полный список обработчиков — в Политике конфиденциальности.',
    'support.faq.q3': 'Когда приложение будет доступно?',
    'support.faq.a3': 'Сейчас мы в бета-версии. Присоединяйтесь к списку ожидания, чтобы быть среди первых пользователей.',
    'support.faq.q4': 'Как удалить аккаунт?',
    'support.faq.a4': 'Вы можете удалить свой аккаунт и все связанные данные, написав нам на dmitriybekker@idks.app.',
    'support.faq.q5': 'Какие устройства поддерживаются?',
    'support.faq.a5': 'IDK Wardrobe будет доступен на устройствах iOS и Android.',
    'support.app.title': 'Информация о приложении',
    'support.app.desc': 'IDK Wardrobe — это платформа виртуальных примерок на базе ИИ, которая позволяет увидеть, как одежда выглядит на вас, до покупки.',
    'footer.support': 'Поддержка',

    // Business / B2B page
    'business.hero.badge': 'Ранний доступ · По приглашениям',
    'business.hero.title.line1': 'Инфраструктура виртуальной примерки',
    'business.hero.title.line2': 'для моды.',
    'business.hero.subtitle': 'Готовый API, превращающий любой fashion-каталог в фотореалистичную виртуальную примерку. На базе передовых AI-моделей. Доступно для дизайн-партнёров в Q3 2026.',
    'business.hero.cta.primary': 'Запросить доступ к API',
    'business.hero.cta.secondary': 'Пример кода',
    'business.hero.credibility': 'На передовых AI-моделях  ·  Enterprise-облако  ·  SLA 99.9%',
    'business.pricing.launchBadge': 'Стартовые цены · с Q3 2026',
    'business.nav.requestAccess': 'Получить доступ к API',

    'business.why.title.part1': 'Сделано иначе.',
    'business.why.title.part2': 'Специально для моды.',
    'business.why.card1.title': 'Фотореалистичное качество',
    'business.why.card1.text': 'Шёлк, кружево, прозрачные ткани, сложные узоры — там, где обычный try-on проваливается. На базе передовых AI-моделей.',
    'business.why.card2.title': 'Готов к масштабу',
    'business.why.card2.text': 'Задержка менее 3 секунд. Авто-масштабирование от 100 до 10M+ примерок/мес. SLA 99.9%.',
    'business.why.card3.title': 'Для разработчиков',
    'business.why.card3.text': 'Готовый REST API. SDK для JavaScript, Python, Swift. Интеграция за 5 минут. Честная документация.',

    'business.how.title.part1': 'От идеи до продакшена за',
    'business.how.title.part2': '5 минут.',
    'business.how.step1.title': 'Получите API-ключ',
    'business.how.step1.text': 'Регистрация. Мгновенно получаете API-ключ. Бесплатный тариф включён.',
    'business.how.step2.title': 'Отправьте две картинки',
    'business.how.step2.text': 'POST-запрос с изображением одежды и модели на наш endpoint.',
    'business.how.step3.title': 'Получите фотореалистичную примерку',
    'business.how.step3.text': 'Отображайте результат в приложении, на сайте или в мобильном магазине.',
    'business.how.codeNote': 'Та же инфраструктура, что работает в нашем потребительском приложении IDK:W2W — сейчас в private beta с fashion-креаторами.',

    'business.useCases.title.part1': 'Что можно',
    'business.useCases.title.part2': 'построить.',
    'business.useCases.card1.title': 'Страницы товаров e-commerce',
    'business.useCases.card1.text': 'Добавьте примерку к каждому товару. Меньше возвратов. Больше конверсии.',
    'business.useCases.card1.audience': 'DTC-бренды · Shopify · Mid-market ритейл',
    'business.useCases.card2.title': 'Мобильные шопинг-приложения',
    'business.useCases.card2.text': 'Нативные iOS и Android SDK. Real-time примерка, без webview.',
    'business.useCases.card2.audience': 'Нативные приложения · Маркетплейсы',
    'business.useCases.card3.title': 'Киоски в офлайн-ритейле',
    'business.useCases.card3.text': 'Интерактивные примерочные. Просмотр каталога без расхода материалов.',
    'business.useCases.card3.audience': 'Омниканальный ритейл · Флагманские магазины',
    'business.useCases.card4.title': 'White-label платформы',
    'business.useCases.card4.text': 'Кастомные модели, дообученные на вашем каталоге. Выделенная инфраструктура.',
    'business.useCases.card4.audience': 'Люксовые дома · Enterprise',

    'business.pricing.title.part1': 'Простые,',
    'business.pricing.title.part2': 'прозрачные тарифы.',
    'business.pricing.subtitle': 'Полные цены — с общим релизом. Дизайн-партнёрам — первые 3 месяца бесплатно.',
    'business.pricing.popular': 'Популярный',
    'business.pricing.tier1.name': 'Developer',
    'business.pricing.tier1.price': 'От $99',
    'business.pricing.tier1.period': '/мес',
    'business.pricing.tier1.descriptor': 'Для инди-разработчиков и DTC-брендов',
    'business.pricing.tier1.f1': '1 000 примерок/мес включено',
    'business.pricing.tier1.f2': 'Полный доступ к REST API',
    'business.pricing.tier1.f3': 'SDK для JS, Python, Swift',
    'business.pricing.tier1.f4': 'Community-поддержка',
    'business.pricing.tier1.cta': 'В список ожидания',
    'business.pricing.tier2.name': 'Growth',
    'business.pricing.tier2.price': 'От $1 499',
    'business.pricing.tier2.period': '/мес',
    'business.pricing.tier2.descriptor': 'Для растущих ритейлеров',
    'business.pricing.tier2.f1': '10 000+ примерок/мес',
    'business.pricing.tier2.f2': 'Shopify-приложение + админ-панель',
    'business.pricing.tier2.f3': 'Инструменты A/B-тестов',
    'business.pricing.tier2.f4': 'Приоритетная поддержка + SLA',
    'business.pricing.tier2.cta': 'В список ожидания',
    'business.pricing.tier3.name': 'Enterprise',
    'business.pricing.tier3.price': 'По запросу',
    'business.pricing.tier3.period': '',
    'business.pricing.tier3.descriptor': 'Для люксовых брендов и маркетплейсов',
    'business.pricing.tier3.f1': 'Безлимит примерок',
    'business.pricing.tier3.f2': 'Выделенная инфраструктура',
    'business.pricing.tier3.f3': 'Дообучение моделей под ваш каталог',
    'business.pricing.tier3.f4': 'SOC 2 · DPA · On-premise',
    'business.pricing.tier3.cta': 'Связаться с фаундерами',

    'business.waitlist.title.part1': 'Станьте одним из первых',
    'business.waitlist.title.part2': '20 дизайн-партнёров.',
    'business.waitlist.subtitle': 'Ранний доступ включает 3 месяца бесплатно, прямую связь с командой основателей и приоритет в формировании roadmap.',
    'business.form.email': 'Рабочий email',
    'business.form.email.placeholder': 'you@company.com',
    'business.form.company': 'Компания',
    'business.form.company.placeholder': 'Acme Apparel',
    'business.form.useCase': 'Что вы строите?',
    'business.form.optional': '(необязательно)',
    'business.form.useCase.placeholder': 'Кратко опишите ваш use-case…',
    'business.form.submit': 'Забронировать место',
    'business.form.submitting': 'Сохраняем…',
    'business.form.disclaimer': 'Мы напишем, когда откроется доступ к API. Никакого спама.',
    'business.form.success.title': 'Вы в списке.',
    'business.form.success.message': 'Мы напишем, когда откроется доступ к API.',
    'business.form.error.duplicate': 'Этот email уже в B2B-списке ожидания.',
    'business.form.error.generic': 'Что-то пошло не так. Попробуйте снова.',

    'business.faq.title': 'Вопросы.',
    'business.faq.q1': 'Чем это отличается от обычных виджетов виртуальной примерки?',
    'business.faq.a1': 'Мы — инфраструктура, а не виджет. REST API + SDK, которые встраиваются куда угодно: веб, мобильное приложение, киоск в магазине. Та же базовая технология, что и в consumer-приложении IDK:W2W, но как платформа.',
    'business.faq.q2': 'Какие ИИ-модели вы используете?',
    'business.faq.a2': 'Премиум-тариф работает на передовых мультимодальных AI-моделях. Для бюджетных кейсов — собственный диффузионный пайплайн. Enterprise-клиентам доступно дообучение моделей под их каталог.',
    'business.faq.q3': 'Когда открывается доступ к API?',
    'business.faq.a3': 'Программа дизайн-партнёров — Q3 2026. Общая доступность — Q4 2026. Запишитесь, чтобы получить ранний доступ.',
    'business.faq.q4': 'Соответствуете ли вы GDPR?',
    'business.faq.a4': 'Да. Мы обрабатываем изображения только в памяти, не храним их после ответа и предоставляем DPA для клиентов из ЕС.',
    'business.faq.q5': 'Можно ли self-host?',
    'business.faq.a5': 'On-premise развёртывание доступно Enterprise-клиентам. Свяжитесь с нами.',
    'business.faq.q6': 'Кто стоит за IDK?',
    'business.faq.a6': 'IDK — независимая команда в сфере fashion-tech, строим AI-инфраструктуру для виртуальной примерки. Наше потребительское приложение IDK:W2W сейчас в private beta. Юрлицо в США оформляем в Q2 2026 — до этого момента контракты идут через операционное лицо. Design-партнёры получают прямой доступ к фаундерам.',

    'business.final.title.part1': 'Готовы',
    'business.final.title.part2': 'строить?',
    'business.final.docs': 'Или почитайте нашу документацию',

    'footer.b2bCrosslink': 'Строите инфраструктуру для ритейла?',
    'footer.b2bCrosslink.cta': 'Посетите нашу B2B-платформу →',
  },
  kz: {
    // Navbar
    'nav.technology': 'Технология',
    'nav.marketplace': 'Discover',
    'nav.founders': 'Негізін қалаушылар',
    'nav.business': 'Бизнеске',
    'nav.contact': 'Байланыс',
    'nav.joinWaitlist': 'Тіркелу',
    
    // Hero
    'hero.badge': 'Бета қол жетімді',
    'hero.title': 'Киер алдында байқап көріңіз.',
    'hero.subtitle': 'AI-мен жұмыс істейтін сіздің цифрлық егізіңіз. Сән болашағын сезіну үшін эксклюзивті бетаға қосылыңыз.',
    'hero.cta.primary': 'Ерте қол жетімділік алу',
    'hero.cta.secondary': 'Қалай жұмыс істейді',
    'hero.original': 'Түпнұсқа',
    'hero.avatar': 'IDK Аватар',
    'hero.wearingNow': 'Қазір киіп жүрсіз',
    'hero.blackBlazer': 'Қара блейзер',
    'hero.trust.location': '🇰🇿 Алматыда орналасқан',
    'hero.trust.gemini': '⚡ Алдыңғы қатарлы AI негізінде',
    'hero.trust.launch': '🚀 2026 жылы іске қосылу',
    'hero.store.appStore': '📱 App Store-да жақын арада',
    'hero.store.googlePlay': '📱 Google Play-де жақын арада',
    
    // Validation Bar
    'validation.onWaitlist': 'күту тізімінде',
    
    // Technology
    'tech.title.part1': 'Сиқырдың артындағы',
    'tech.title.part2': 'технология.',
    'tech.subtitle': 'Озық AI жоғары сәнмен кездеседі. Сиқыр сияқты сезілетін киіп көру технологиясын сезініңіз.',
    'tech.feature1.title': 'AI Виртуалды киіп көру',
    'tech.feature1.desc': 'Біздің диффузиялық модельдер секундтарда фотореалистік киіп көруді жасайды. Кез келген киімнің сіздің цифрлық егізіңізде қалай көрінетінін көріңіз.',
    'tech.feature2.title': 'Гардеробты цифрландыру',
    'tech.feature2.desc': 'Кез келген киім затының фотосын түсіріңіз. Біздің AI оны араластыру үшін тамаша шығарады.',
    'tech.feature3.title': 'Пікір алыңыз',
    'tech.feature3.desc': 'Достарыңызбен образдарды бөлісіңіз және лезде реакциялар алыңыз. Керемет пе, жоқ па?',
    'tech.livePoll': 'Сауалнама',
    'tech.votes': 'дауыс',
    
    // Marketplace
    'marketplace.title.part1': 'Сенімділікпен',
    'marketplace.title.part2': 'сатып алыңыз.',
    'marketplace.subtitle': 'Сатып алмас бұрын киіп көріңіз. Біздің каталог люкс заттардың аватарыңызда қалай көрінетінін көрсетеді.',
    'marketplace.comingSoon': 'Брендтермен серіктестік жақында',
    'marketplace.comingSoonBadge': 'Жақында',
    'marketplace.previewBanner': 'Discover алдын ала қарау · Сатып алу 2026 жылдың Q4-нде ашылады',
    'marketplace.previewBadge': 'Алдын ала',

    // How It Works
    'how.title.part1': 'Цифрлық қосарыңа дейін',
    'how.title.part2': 'үш қадам.',
    'how.step1.title': 'Екі фото жүктеңіз',
    'how.step1.desc': 'Бір толық бой және бір жақыннан түсірілген бет. Бізге қажет нәрсе осы.',
    'how.step2.title': 'AI аватарыңызды жасайды',
    'how.step2.desc': 'Біздің diffusion модельдер 20 секундтан аз уақытта фотореалистік цифрлық қосарды жасайды.',
    'how.step3.title': 'Кез келген нәрсені өлшеп көріңіз',
    'how.step3.desc': 'Гардеробыңыздан немесе кез келген дүкеннен кез келген затты таңдаңыз — оны өзіңізде бірден көріңіз.',

    // Privacy
    'privacy.title.part1': 'Фотоларыңыз',
    'privacy.title.part2': 'өзіңіздікі болып қалады.',
    'privacy.subtitle': 'Біз IDK-ны құрғанда privacy негіз болды, соңынан ойластырылған нәрсе емес.',
    'privacy.card1.title': 'Гардеробыңыз — тек сіздікі',
    'privacy.card1.desc': 'Гардеробтағы фотолар тек сізге көрінеді. Аватар сіз бөліскен жерде ғана пайда болады — кәдімгі профиль фотосы сияқты.',
    'privacy.card2.title': 'AI үйрету үшін қолданылмайды',
    'privacy.card2.desc': 'Фотолар HTTPS арқылы Firebase-бэкендімізге және AI үшін Google Cloud AI (Gemini)-ге жіберіледі — enterprise-шартымыз бойынша Google оларды сақтамайды және модельдерді үйретпейді.',
    'privacy.card3.title': 'Кез келген уақытта жою',
    'privacy.card3.desc': 'Бір тап — және аватарыңыз бен барлық деректер жойылды. Сұрақсыз.',

    // FAQ (consumer)
    'faq.title': 'Жиі қойылатын сұрақтар.',
    'faq.q1': 'IDK қашан іске қосылады?',
    'faq.a1': 'Ерте қатынасты 2026 жылдың Q3-нде ашамыз. Бірінші болып шақыру алу үшін waitlist-қа жазылыңыз.',
    'faq.q2': 'Қандай құрылғыларда жұмыс істейді?',
    'faq.a2': 'iOS 16+ және Android 12+. iPhone/iPad қосымшасы алдымен іске қосылады, Android — 2026 жылдың Q4-нде.',
    'faq.q3': 'Өлшеу қаншалықты дәл?',
    'faq.a3': 'Сән үшін арнайы fine-tuning-мен алдыңғы қатарлы diffusion модельдерді қолданамыз. Өлшеулер шынайы матаның түсуін, дене пропорцияларын және жарықтандыруды көрсетеді. Шынайы өлшеу бөлмесіне ең жақын нәрсе.',
    'faq.q4': 'Менің деректерім қауіпсіз бе?',
    'faq.a4': 'Иә. Фотолар қорғалған HTTPS арнасы арқылы Firebase-бэкендімізге және AI өңдеуі үшін Google Gemini-ге жіберіледі — Google оларды сақтамайды және модельдерін үйретпейді. Гардероб фотолары тек сізге көрінеді; барлығын бір түртумен жоюға болады.',
    'faq.q5': 'IDK тегін бола ма?',
    'faq.a5': 'Иә — тегін тариф бар (3 жеке өлшеу, 2 толық образ, 1 аватар — барлығы өмірде). Pro ($19.99/ай) айына 50 жеке өлшеу мен 25 образ + AI-стилист береді. Ultra ($39.99/ай) лимиттерді екі есе арттырады, AI-чемодан жинауын шексіз етеді және басым инфрақұрылымда жұмыс істейді.',
    
    // Social
    'social.title.part1': 'Сән — бұл',
    'social.title.part2': 'қарым-қатынас.',
    'social.subtitle': 'Стиліңізбен бөлісіңіз, жаңа образдар ашыңыз және достарыңыздың киімдерін киіп көріңіз — бәрі бір жерде.',
    'social.stories': 'Тарихтар',
    'social.you': 'Сіз',
    'social.feature1.title': 'Достар қосыңыз',
    'social.feature1.desc': 'Достарыңызбен байланысыңыз және сән шеңберін құрыңыз. Олардың стилін көріңіз және шабыттаныңыз.',
    'social.feature2.title': 'Образдарды бөлісіңіз',
    'social.feature2.desc': 'Киімдеріңізді тарихтар ретінде жариялаңыз. Достарыңыздан реакциялар мен пікірлер алыңыз.',
    'social.feature3.title': 'Достардың киімін киіп көру',
    'social.feature3.desc': 'Лентадағы кез келген киімді цифрлық егізіңізде көріңіз. Сатып алмас немесе алыс бұрын киіп көріңіз.',
    'social.tryOn.title': 'Олардың киімін',
    'social.tryOn.you': 'өзіңізде көріңіз',
    'social.tryOn.desc': 'Лентадан керемет лук байқадыңыз ба? Бір түрту — ол сіздің аватарыңызда. Сізге сәйкес келе ме деп болжамау керек.',
    'social.tryOn.comingSoon': 'Жақында',
    'social.tryOn.friendLook': 'Достың образы',
    'social.tryOn.onAvatar': 'Сіздің аватарыңызда',
    
    // Founders
    'founders.title': 'Негізін қалаушылар',
    'founders.subtitle': 'Екі негізін қалаушы киім сатып алу тәсілінің болашағын құрып жатыр',
    'founders.bio.dmitriy': 'Тең құрылтайшы және CEO. Бір фотодан цифрлық қосарыңды жасайтын AI виртуалды өлшеу инфрақұрылымын құрады.',
    'founders.bio.arina': 'Өнім және дизайн. IDK қалай сезілетінін қалыптастырады — бірінші тептен әр өлшеуге дейін.',
    'founders.social.comingSoon': 'Жақында',
    
    // Footer
    'footer.description': 'AI-мен жұмыс істейтін сән киіп көрудің болашағы.',
    'footer.platform': 'Платформа',
    'footer.company': 'Компания',
    'footer.legal': 'Құқықтық',
    'footer.howItWorks': 'Виртуалды киіп көру',
    'footer.forBrands': 'Цифрлық гардероб',
    'footer.pricing': 'Маркетплейс',
    'footer.aboutUs': 'Біз туралы',
    'footer.careers': 'Мансап',
    'footer.press': 'Баспасөз',
    'footer.privacyPolicy': 'Құпиялылық',
    'footer.termsOfService': 'Шарттар',
    'footer.cookiePolicy': 'Cookies',
    'footer.madeWith': 'Жасалған',
    'footer.allRights': 'Барлық құқықтар қорғалған.',
    
    // Cookie Banner
    'cookie.message': 'Біз тәжірибеңізді жақсарту үшін cookies қолданамыз. Жалғастыра отырып, сіз біздің',
    'cookie.policy': 'Cookie саясатымен келісесіз',
    'cookie.decline': 'Бас тарту',
    'cookie.accept': 'Барлығын қабылдау',
    
    // Waitlist Dialog
    'waitlist.title': 'Күту тізімі',
    'waitlist.subtitle': 'Сән киіп көрудің болашағын бірінші болып сезінетіндер қатарында болыңыз.',
    'waitlist.email': 'Email енгізіңіз',
    'waitlist.submit': 'Ерте қол жетімділік алу',
    'waitlist.submitting': 'Тіркелуде...',
    'waitlist.success.title': 'Сіз тізімдесіз!',
    'waitlist.success.message': 'Бета қол жетімділік ашылғанда хабарлаймыз.',
    
    // Contact
    'contact.title': 'Бізбен байланысыңыз',
    'contact.subtitle': 'IDK Wardrobe туралы сұрақтарыңыз бар ма? Біз сізден естуге қуаныштымыз.',
    'contact.getInTouch': 'Байланысу',
    'contact.email': 'Email',
    'contact.phoneCeo': 'Телефон (CEO)',
    'contact.instagram': 'Instagram',
    'contact.linkedin': 'LinkedIn',
    'contact.inquiries': 'Сұраныстар',
    'contact.ceo': 'CEO — Дмитрий Беккер',
    'contact.partnerships': 'Брендтермен серіктестік',
    'contact.press': 'Баспасөз және медиа',
    'contact.faq.title': 'Жиі қойылатын сұрақтар',
    'contact.faq.q1': 'IDK Wardrobe қашан іске қосылады?',
    'contact.faq.a1': 'Біз қазір әзірлеу үстіндеміз және күту тізіміне жазылуды қабылдап жатырмыз. Іске қосу туралы бірінші болып білу және ерте қол жеткізу үшін тізімге қосылыңыз.',
    'contact.faq.q2': 'AI виртуалды киіп көру қалай жұмыс істейді?',
    'contact.faq.a2': 'Біздің AI технологиямыз сіздің фотоңызды талдап, оған киімді шынайы түрде бейнелейді — заттың сізге қалай жарасатынын дәл көресіз.',
    'contact.faq.q3': 'Менің деректерім қауіпсіз бе?',
    'contact.faq.a3': 'Иә, біз құпиялылыққа аса мән береміз. Фотолар HTTPS арқылы Firebase-бэкендімізге және AI үшін Google Gemini-ге жіберіледі — enterprise-шарт бойынша Google оларды сақтамайды және модельдерді үйретпейді. Толық тізім — Құпиялылық саясатында.',
    'contact.faq.q4': 'Брендтер IDK Wardrobe-пен қалай серіктес бола алады?',
    'contact.faq.a4': 'Біз әрқашан инновациялық сән брендтерімен серіктестікке ашықпыз. dmitriybekker@idks.app арқылы хабарласыңыз.',
    
    // 404
    '404.title': '404',
    '404.message': 'Упс! Бет табылмады',
    '404.back': 'Басты бетке оралу',
    
    // Support
    'support.title': 'Қолдау',
    'support.subtitle': 'Көмек керек пе? Біз сіз үшін осындамыз.',
    'support.email.title': 'Email қолдауы',
    'support.email.desc': 'Бізге жазыңыз, біз мүмкіндігінше тез жауап береміз.',
    'support.instagram.title': 'Instagram DM',
    'support.instagram.desc': 'Жылдам қолдау үшін Instagram-да хабарлама жіберіңіз.',
    'support.response.title': 'Жауап уақыты',
    'support.response.desc': 'Біз әдетте 24-48 сағат ішінде жауап береміз.',
    'support.faq.title': 'Жиі қойылатын сұрақтар',
    'support.faq.q1': 'IDK Wardrobe қалай жұмыс істейді?',
    'support.faq.a1': 'IDK Wardrobe сіздің цифрлық егізіңізді жасау үшін озық AI технологиясын пайдаланады. Содан кейін сатып алмас бұрын киімдерді виртуалды түрде киіп көре аласыз.',
    'support.faq.q2': 'Менің деректерім қауіпсіз бе?',
    'support.faq.a2': 'Иә. Деректер HTTPS арқылы Firebase-бэкендке, ал AI үшін фотолар Google Gemini-ге жіберіледі — Google оларды сақтамайды және модельдерді үйретпейді. Толық тізім — Құпиялылық саясатында.',
    'support.faq.q3': 'Қолданба қашан қол жетімді болады?',
    'support.faq.a3': 'Біз қазір бета нұсқасындамыз. IDK Wardrobe қолданушыларының алғашқыларының бірі болу үшін күту тізіміне қосылыңыз.',
    'support.faq.q4': 'Аккаунтты қалай жоюға болады?',
    'support.faq.a4': 'Аккаунтыңызды және барлық байланысты деректерді dmitriybekker@idks.app мекенжайына хабарласу арқылы жоюға болады.',
    'support.faq.q5': 'Қандай құрылғылар қолдау көрсетіледі?',
    'support.faq.a5': 'IDK Wardrobe iOS және Android құрылғыларында қол жетімді болады.',
    'support.app.title': 'Қолданба туралы ақпарат',
    'support.app.desc': 'IDK Wardrobe — бұл сатып алмас бұрын киімнің сізге қалай көрінетінін көруге мүмкіндік беретін AI негізіндегі виртуалды киіп көру платформасы.',
    'footer.support': 'Қолдау',

    // Business / B2B page
    'business.hero.badge': 'Ерте қол жетімділік · Шақыру бойынша',
    'business.hero.title.line1': 'Виртуалды киіп көру инфрақұрылымы',
    'business.hero.title.line2': 'сән үшін.',
    'business.hero.subtitle': 'Кез келген сән каталогын фотореалистік виртуалды киіп көруге айналдыратын дайын API. Озық AI-модельдер негізінде. Дизайн-серіктестер үшін Q3 2026-да қол жетімді.',
    'business.hero.cta.primary': 'API қол жетімділігін сұрау',
    'business.hero.cta.secondary': 'Код мысалы',
    'business.hero.credibility': 'Озық AI-модельдер негізінде  ·  Enterprise бұлт  ·  99.9% SLA',
    'business.pricing.launchBadge': 'Бастапқы бағалар · Q3 2026-дан',
    'business.nav.requestAccess': 'API-ге қол жетімділік сұрау',

    'business.why.title.part1': 'Басқаша құрылған.',
    'business.why.title.part2': 'Сән үшін арнайы.',
    'business.why.card1.title': 'Фотореалистік сапа',
    'business.why.card1.text': 'Жібек, шілтер, мөлдір маталар, күрделі өрнектер — әдеттегі try-on істен шығатын жерде. Озық AI-модельдер негізінде.',
    'business.why.card2.title': 'Масштабқа дайын',
    'business.why.card2.text': '3 секундтан аз кідіріс. 100-ден 10M+ киіп көру/айға дейін авто-масштабтау. 99.9% SLA.',
    'business.why.card3.title': 'Әзірлеушілерге арналған',
    'business.why.card3.text': 'Дайын REST API. JavaScript, Python, Swift үшін SDK. 5 минутта интеграция. Адал құжаттама.',

    'business.how.title.part1': 'Идеядан өндіріске',
    'business.how.title.part2': '5 минутта.',
    'business.how.step1.title': 'API-кілтіңізді алыңыз',
    'business.how.step1.text': 'Тіркеліңіз. API-кілтті бірден алыңыз. Тегін тариф қосылған.',
    'business.how.step2.title': 'Екі сурет жіберіңіз',
    'business.how.step2.text': 'Киім мен модельдің суретін POST арқылы біздің endpoint-ке жіберіңіз.',
    'business.how.step3.title': 'Фотореалистік нәтижені алыңыз',
    'business.how.step3.text': 'Нәтижені қосымшаңызда, сайтыңызда немесе мобильді дүкенде көрсетіңіз.',
    'business.how.codeNote': 'IDK:W2W consumer қосымшамыздағы бірдей инфрақұрылым — қазір fashion-креаторлармен private beta-да.',

    'business.useCases.title.part1': 'Не құруға',
    'business.useCases.title.part2': 'болады.',
    'business.useCases.card1.title': 'E-commerce өнім беттері',
    'business.useCases.card1.text': 'Әр өнімге киіп көруді қосыңыз. Қайтарымдарды азайтыңыз. Конверсияны арттырыңыз.',
    'business.useCases.card1.audience': 'DTC-брендтер · Shopify · Mid-market ритейл',
    'business.useCases.card2.title': 'Мобильді шопинг қосымшалар',
    'business.useCases.card2.text': 'Нативті iOS және Android SDK. Real-time киіп көру, webview жоқ.',
    'business.useCases.card2.audience': 'Нативті қосымшалар · Маркетплейстер',
    'business.useCases.card3.title': 'Офлайн-ритейлдегі киосктер',
    'business.useCases.card3.text': 'Интерактивті киіп көру бөлмелері. Шығынсыз каталог қарау.',
    'business.useCases.card3.audience': 'Омниарналы ритейл · Флагмандық дүкендер',
    'business.useCases.card4.title': 'White-label платформалар',
    'business.useCases.card4.text': 'Каталогыңызға бапталған модельдер. Бөлектелген инфрақұрылым.',
    'business.useCases.card4.audience': 'Люкс брендтер · Enterprise',

    'business.pricing.title.part1': 'Қарапайым,',
    'business.pricing.title.part2': 'мөлдір баға.',
    'business.pricing.subtitle': 'Толық баға жалпы шығарумен ашылады. Дизайн-серіктестерге алғашқы 3 ай тегін.',
    'business.pricing.popular': 'Танымал',
    'business.pricing.tier1.name': 'Developer',
    'business.pricing.tier1.price': '$99-дан',
    'business.pricing.tier1.period': '/айына',
    'business.pricing.tier1.descriptor': 'Инди-әзірлеушілер мен DTC-брендтерге',
    'business.pricing.tier1.f1': 'Айына 1 000 киіп көру',
    'business.pricing.tier1.f2': 'REST API-ге толық қол жетімділік',
    'business.pricing.tier1.f3': 'JS, Python, Swift үшін SDK',
    'business.pricing.tier1.f4': 'Community қолдау',
    'business.pricing.tier1.cta': 'Күту тізіміне',
    'business.pricing.tier2.name': 'Growth',
    'business.pricing.tier2.price': '$1 499-дан',
    'business.pricing.tier2.period': '/айына',
    'business.pricing.tier2.descriptor': 'Өсіп жатқан ритейлерлерге',
    'business.pricing.tier2.f1': 'Айына 10 000+ киіп көру',
    'business.pricing.tier2.f2': 'Shopify қосымшасы + әкімші панелі',
    'business.pricing.tier2.f3': 'A/B-тест құралдары',
    'business.pricing.tier2.f4': 'Басым қолдау + SLA',
    'business.pricing.tier2.cta': 'Күту тізіміне',
    'business.pricing.tier3.name': 'Enterprise',
    'business.pricing.tier3.price': 'Жеке',
    'business.pricing.tier3.period': '',
    'business.pricing.tier3.descriptor': 'Люкс брендтер мен маркетплейстерге',
    'business.pricing.tier3.f1': 'Шексіз киіп көру',
    'business.pricing.tier3.f2': 'Бөлектелген инфрақұрылым',
    'business.pricing.tier3.f3': 'Каталогыңыз бойынша модельдерді баптау',
    'business.pricing.tier3.f4': 'SOC 2 · DPA · On-premise',
    'business.pricing.tier3.cta': 'Негізін қалаушылармен сөйлесу',

    'business.waitlist.title.part1': 'Алғашқы',
    'business.waitlist.title.part2': '20 дизайн-серіктесінің бірі болыңыз.',
    'business.waitlist.subtitle': 'Ерте қол жетімділік: 3 ай тегін, негізін қалаушы командамен тікелей байланыс және roadmap-та басымдық.',
    'business.form.email': 'Жұмыс email',
    'business.form.email.placeholder': 'you@company.com',
    'business.form.company': 'Компания',
    'business.form.company.placeholder': 'Acme Apparel',
    'business.form.useCase': 'Не құрып жатырсыз?',
    'business.form.optional': '(міндетті емес)',
    'business.form.useCase.placeholder': 'Use-case-ыңызды қысқаша сипаттаңыз…',
    'business.form.submit': 'Орынды брондау',
    'business.form.submitting': 'Сақтаудамыз…',
    'business.form.disclaimer': 'API қол жетімділігі ашылғанда хабарлаймыз. Спам жоқ.',
    'business.form.success.title': 'Сіз тізімдесіз.',
    'business.form.success.message': 'API қол жетімділігі ашылғанда хабарлаймыз.',
    'business.form.error.duplicate': 'Бұл email B2B күту тізімінде бар.',
    'business.form.error.generic': 'Бірдеңе дұрыс болмады. Қайталап көріңіз.',

    'business.faq.title': 'Сұрақтар.',
    'business.faq.q1': 'Бұл әдеттегі виртуалды киіп көру виджеттерінен немен ерекшеленеді?',
    'business.faq.a1': 'Біз — инфрақұрылыммыз, виджет емес. Веб, мобильді қосымша, дүкендегі киоск — кез келген жерге орнатылатын REST API + SDK. IDK:W2W consumer қосымшасындағы сол негізгі технология, бірақ платформа ретінде.',
    'business.faq.q2': 'Қандай ИИ-модельдерді пайдаланасыздар?',
    'business.faq.a2': 'Премиум тариф озық мультимодальдық AI-модельдерде жұмыс істейді. Бюджеттік кейстер үшін — өзіміздің диффузиялық пайплайн. Enterprise клиенттерге каталог бойынша баптау қол жетімді.',
    'business.faq.q3': 'API қашан ашылады?',
    'business.faq.a3': 'Дизайн-серіктестер бағдарламасы — Q3 2026. Жалпы қол жетімділік — Q4 2026. Ерте қол жетімділік үшін тіркеліңіз.',
    'business.faq.q4': 'GDPR-ге сай келесіз бе?',
    'business.faq.a4': 'Иә. Біз суреттерді тек жадыда өңдейміз, жауап терезесінен кейін сақтамаймыз және ЕО клиенттеріне DPA береміз.',
    'business.faq.q5': 'Self-host жасауға бола ма?',
    'business.faq.a5': 'On-premise орналастыру Enterprise клиенттерге қол жетімді. Бізбен байланысыңыз.',
    'business.faq.q6': 'IDK артында кім тұр?',
    'business.faq.a6': 'IDK — AI виртуалды киіп көру инфрақұрылымын құратын тәуелсіз fashion-tech команда. IDK:W2W consumer қосымшамыз қазір private beta-да. АҚШ-та заңды тұлғаны Q2 2026-да рәсімдейміз — оған дейін барлық келісімшарттар операциялық тұлға арқылы өтеді. Design-серіктестер фаундерлерге тікелей қол жетімді.',

    'business.final.title.part1': 'Құруға',
    'business.final.title.part2': 'дайынсыз ба?',
    'business.final.docs': 'Немесе құжаттаманы оқыңыз',

    'footer.b2bCrosslink': 'Ритейлге арналған инфрақұрылым құрып жатырсыз ба?',
    'footer.b2bCrosslink.cta': 'B2B платформамызға өтіңіз →',
  },
};

const getLanguageSwitchSnapshot = () => {
  if (typeof window === 'undefined' || window.scrollY <= 8) return null;

  const navbar = document.querySelector('nav');
  const navbarHeight = navbar instanceof HTMLElement ? navbar.getBoundingClientRect().height : 80;
  const sampleX = Math.min(Math.max(window.innerWidth / 2, 24), window.innerWidth - 24);
  const sampleY = Math.min(navbarHeight + 24, window.innerHeight - 24);
  const source = document.elementFromPoint(sampleX, sampleY);

  if (!(source instanceof HTMLElement)) return null;

  const anchor =
    source.closest<HTMLElement>('section, article, [data-language-anchor], h1, h2, h3, p, li, div') ?? source;

  return {
    anchor,
    top: anchor.getBoundingClientRect().top,
  };
};

const restoreLanguageSwitchSnapshot = (
  snapshot: { anchor: HTMLElement; top: number } | null,
) => {
  if (!snapshot) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!document.body.contains(snapshot.anchor)) return;

      const nextTop = snapshot.anchor.getBoundingClientRect().top;
      const delta = nextTop - snapshot.top;

      if (Math.abs(delta) > 1) {
        window.scrollBy({ top: delta, behavior: 'auto' });
      }
    });
  });
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'en';
  });

  const setLanguage = (lang: Language) => {
    if (lang === language) return;

    const snapshot = getLanguageSwitchSnapshot();
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    restoreLanguageSwitchSnapshot(snapshot);
  };

  const t = (key: string): string => {
    const value = translations[language][key];
    return value !== undefined ? value : key;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
